import { collection, doc, serverTimestamp, setDoc, addDoc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

const COLLECTION = 'reservationLogs';

// Create a new document reference with a random ID for use in transactions/batches
export const newReservationLogRef = () => doc(collection(db, COLLECTION));

// Write a log entry within a transaction or batch: tx.set(ref, payload)
export const writeReservationLogTx = (tx, ref, payload) => {
  if (!db || !tx || !ref) return;
  tx.set(ref, { ...payload, createdAt: serverTimestamp() });
};

// Convenience method to write a log entry outside of a transaction
export const writeReservationLog = async (payload) => {
  if (!db) throw new Error('Database not initialized');
  const col = collection(db, COLLECTION);
  await addDoc(col, { ...payload, createdAt: serverTimestamp() });
};
// Query logs with optional filters
export const listReservationLogs = async ({ eventId, layoutId, boothId, types = [], max = 100 } = {}) => {
  if (!db) throw new Error('Database not initialized');
  const col = collection(db, COLLECTION);
  // Build dynamic query constraints
  let qConstraints = [];
  if (eventId) qConstraints.push(where('eventId', '==', eventId));
  if (layoutId) qConstraints.push(where('layoutId', '==', layoutId));
  if (boothId) qConstraints.push(where('boothId', '==', boothId));
  // Filter by type post-query to avoid requiring an extra composite index
  // for (eventId, layoutId, type, createdAt).
  let qRef = query(col, ...qConstraints, orderBy('createdAt', 'desc'), limit(max));
  const snap = await getDocs(qRef);
  let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (Array.isArray(types) && types.length > 0) {
    const setTypes = new Set(types);
    rows = rows.filter(r => setTypes.has(r.type));
  }
  return rows;
};

// List distinct log types available for a selected event.
export const listReservationLogTypesByEvent = async ({ eventId, max = 1000 } = {}) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId) return [];
  const col = collection(db, COLLECTION);
  const qRef = query(col, where('eventId', '==', eventId), limit(max));
  const snap = await getDocs(qRef);
  const typeSet = new Set();
  snap.forEach(d => {
    const t = (d.data() || {}).type;
    if (typeof t === 'string' && t.trim()) typeSet.add(t.trim());
  });
  return Array.from(typeSet).sort((a, b) => a.localeCompare(b));
};


/*
Schema (reservationLogs):
  - type: string (e.g., 'expired-cleanup', 'expired-cleanup-on-hold', 'released', ...)
  - reservationId: string (eventId_layoutId_boothId or Firestore doc id)
  - eventId, layoutId, boothId: string
  - previousStatus: 'Pending' | 'On-Hold' | 'Reserved' | ...
  - previousProfileId: string | null
  - previousSessionId: string | null
  - previousCreatedAt: Timestamp | Date | null
  - previousExpiresAt: Timestamp | Date | null
  - expiredAt: serverTimestamp() when deletion executed
  - triggeredBy: string (e.g., 'reserveBooth', 'clearExpiredHolds')
  - triggeredByProfileId: string | null (actor attempting new reservation)
  - metadata: object (optional: waitMinutes, reason, etc.)
  - createdAt: serverTimestamp() (log entry timestamp)
*/
